import { useCallback } from 'react';

import {
  extractInstructionsFromPlaceholders,
  mergeStructureInstructionsIntoData,
  mergeTextIntoData,
  removeInstructionsFromData,
  removeUnavailableInstructionsFromData,
} from 'src/components/RichSlate';
import { useBrandVoicesActions } from 'src/store';

import { usePrepareStructureInstructionsDefaults } from './usePrepareStructureInstructionsDefaults';
import { preparePromptFieldStructureInstructions } from './usePromptFieldStructureInstructions';

export const useActualizeStructureInstructionsValue = () => {
  const prepareStructureInstructionsDefaults = usePrepareStructureInstructionsDefaults();
  const brandVoicesActions = useBrandVoicesActions();

  return useCallback(
    ({
      generationTool,
      language,
      instructionTypes,
      currentValue,
      mergeDefaults,
      generalStructureInstructions,
    }) => {
      const brandVoicesWithTerms = brandVoicesActions.getVoicesWithTerms({ language });
      const customFormats = brandVoicesActions.getDefaultVoiceCustomFormats({ language });

      const availableInstructions = preparePromptFieldStructureInstructions({
        generationTool,
        brandVoicesWithTerms,
        customFormats,
        instructionTypes,
      });

      // remove all un-available instructions from the current value
      let actualizedValue = removeUnavailableInstructionsFromData(
        currentValue,
        availableInstructions
      );

      if (generalStructureInstructions?.placeholders) {
        // extract instructions from "general" structure instructions and merge them with the current value
        const instructionsInGeneralInstructions = extractInstructionsFromPlaceholders({
          placeholders: Object.values(generalStructureInstructions.placeholders),
          customFormats,
          brandVoicesWithTerms,
        });
        actualizedValue = mergeStructureInstructionsIntoData(
          actualizedValue,
          instructionsInGeneralInstructions
        );

        // extract text from "general" structure instructions and merge it with the current value
        const generalInstructionsText = removeInstructionsFromData(
          generalStructureInstructions,
          instructionsInGeneralInstructions
        );

        actualizedValue = mergeTextIntoData(actualizedValue, generalInstructionsText);
      }

      // end here, if we don't need to merge "defaults"
      if (!mergeDefaults) {
        return actualizedValue;
      }

      // prepare "default" structure instructions
      const defaults = prepareStructureInstructionsDefaults({
        generationTool,
        language,
        instructionTypes,
      });

      // merge with possibly existing "default" structure instructions
      return mergeStructureInstructionsIntoData(actualizedValue, defaults.initialInstructions);
    },
    [brandVoicesActions, prepareStructureInstructionsDefaults]
  );
};
